import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AllowedDomainsManager from './AllowedDomainsManager';
import Pagination from './Pagination';

const AdminMerchantManagement = () => {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [merchantPage, setMerchantPage] = useState(1);
  const MERCHANT_PAGE_SIZE = 10;
  const [form, setForm] = useState({
    businessName: '', domain: '', preferredBank: 'ICICI',
    provider: 'ICICI', merchantId: '', merchantVpa: '',
    apiKey: '', merchantSalt: '', successUrl: '',
    failureUrl: '', apiUrl: '', callbackUrl: '', webhookUrl: '',
    privateIp: '', bankEndpoint: '', username: '',
    iciciPublicKeyName: '', merchantPrivateKeyName: '', merchantCertificateName: '',
    // Payout CIB Config
    payoutAggrId: '', payoutAggrName: '', payoutCorpId: '',
    payoutUserId: '', payoutUrn: '', payoutDebitAcc: '',
    payoutWorkflowReqd: 'N', payoutProvider: 'ICICI_CIB',
    cashfreeClientId: '', cashfreeSecretKey: '', cashfreeApiVersion: '',
    payoutPerTxnLimit: '', payoutDailyLimit: '',
    payoutApiKey: '', payoutApiUrl: '', payoutPublicKeyName: ''
  });

  const getToken = () => localStorage.getItem('token');
  const headers = () => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => { fetchMerchants(); }, []);

  const fetchMerchants = async () => {
    try {
      const res = await axios.get('/api/admin/merchants', { headers: headers() });
      if (res.data.success) {
        setMerchants(res.data.data || []);
        return res.data.data || [];
      }
    } catch (e) { console.error('Failed to fetch merchants:', e); }
    finally { setLoading(false); }
    return [];
  };

  const resetForm = () => {
    setForm({
      businessName: '', domain: '', preferredBank: 'ICICI',
      provider: 'ICICI', merchantId: '', merchantVpa: '',
      apiKey: '', merchantSalt: '', successUrl: '',
      failureUrl: '', apiUrl: '', callbackUrl: '', webhookUrl: '',
      privateIp: '', bankEndpoint: '', username: '',
      iciciPublicKeyName: '', merchantPrivateKeyName: '', merchantCertificateName: '',
      payoutAggrId: '', payoutAggrName: '', payoutCorpId: '',
      payoutUserId: '', payoutUrn: '', payoutDebitAcc: '',
      payoutWorkflowReqd: 'N', payoutProvider: 'ICICI_CIB',
      payoutPerTxnLimit: '', payoutDailyLimit: '',
      payoutApiKey: '', payoutApiUrl: '', payoutPublicKeyName: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const openAdd = () => { resetForm(); setShowForm(true); };

  const openEdit = async (m) => {
    // Always fetch fresh data by ID to get all fields including bankConfig, privateIp etc.
    let merchantData = m;
    try {
      const res = await axios.get(`/api/admin/merchants/${m.id}`, { headers: headers() });
      if (res.data.success) merchantData = res.data.data;
    } catch (e) { console.warn('Failed to fetch fresh merchant data, using list data'); }

    console.log('[openEdit] merchant data:', JSON.stringify(merchantData, null, 2));
    let bankConfig = {};
    try {
      if (merchantData.bankConfig) {
        let parsed = merchantData.bankConfig;
        while (typeof parsed === 'string') { parsed = JSON.parse(parsed); }
        bankConfig = parsed || {};
      }
    } catch (e) { console.warn('Failed to parse bankConfig:', e); }

    // Fetch payout config for this merchant
    let payoutConfig = {};
    try {
      const payoutRes = await axios.get(`/api/admin/payout-config/${merchantData.id}`, { headers: headers() });
      if (payoutRes.data.success && payoutRes.data.data) {
        payoutConfig = payoutRes.data.data;
      }
    } catch (e) { console.warn('No payout config found for merchant, using defaults'); }

    setForm({
      businessName: merchantData.businessName || '',
      domain: merchantData.domain || '',
      preferredBank: merchantData.preferredBank || 'ICICI',
      provider: bankConfig.provider || merchantData.preferredBank || 'ICICI',
      merchantId: bankConfig.merchantId || '',
      merchantVpa: bankConfig.merchantVpa || merchantData.upiId || '',
      apiKey: bankConfig.apiKey || '',
      apiSecret: bankConfig.apiSecret || '',
      apiVersion: bankConfig.apiVersion || '',
      merchantSalt: bankConfig.merchantSalt || '',
      successUrl: bankConfig.successUrl || '',
      failureUrl: bankConfig.failureUrl || '',
      apiUrl: bankConfig.apiUrl || (bankConfig.environment ? bankConfig.environment : ''),
      callbackUrl: bankConfig.callbackUrl || '',
      webhookUrl: merchantData.webhookUrl || '',
      privateIp: merchantData.privateIp || '',
      bankEndpoint: merchantData.bankEndpoint || '',
      username: merchantData.username || '',
      iciciPublicKeyName: merchantData.iciciPublicKeyName || '',
      merchantPrivateKeyName: merchantData.merchantPrivateKeyName || '',
      merchantCertificateName: merchantData.merchantCertificateName || '',
      payoutAggrId: payoutConfig.aggrId || '',
      payoutAggrName: payoutConfig.aggrName || '',
      payoutCorpId: payoutConfig.corpId || '',
      payoutUserId: payoutConfig.userId || '',
      payoutUrn: payoutConfig.urn || '',
      payoutDebitAcc: payoutConfig.debitAcc || '',
      payoutWorkflowReqd: payoutConfig.workflowReqd || 'N',
      payoutProvider: payoutConfig.preferredProvider || 'ICICI_CIB',
      payoutPerTxnLimit: payoutConfig.perTransactionLimit || '',
      payoutDailyLimit: payoutConfig.dailyLimit || '',
      payoutApiKey: payoutConfig.payoutApiKey || '',
      payoutApiUrl: payoutConfig.payoutApiUrl || '',
      payoutPublicKeyName: payoutConfig.payoutPublicKeyName || '',
      cashfreeClientId: payoutConfig.cashfreeClientId || '',
      cashfreeSecretKey: payoutConfig.cashfreeSecretKey || '',
      cashfreeApiVersion: payoutConfig.cashfreeApiVersion || ''
    });
    setEditingId(merchantData.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const buildBankConfig = () => {
    const base = {
      provider: form.provider,
      apiKey: form.apiKey,
    };

    if (form.provider === 'EASEBUZZ') {
      return {
        ...base,
        merchantSalt: form.merchantSalt,
        environment: form.apiUrl || 'uat',
      };
    }

    if (form.provider === 'CASHFREE') {
      return {
        ...base,
        apiSecret: form.apiSecret || '',
        callbackUrl: form.callbackUrl || '',
        apiVersion: form.apiVersion || '',
      };
    }

    // ICICI / HDFC / KOTAK / AXIS / others
    return {
      ...base,
      merchantId: form.merchantId,
      apiUrl: form.apiUrl,
      merchantVpa: form.merchantVpa,
      callbackUrl: form.callbackUrl,
      webhookUrl: form.webhookUrl,
    };
  };

  const saveMerchant = async () => {
    if (!form.businessName.trim()) { alert('Business name is required'); return; }
    if (!form.domain.trim()) { alert('Payment Domain is required'); return; }
    // if (!form.merchantId.trim()) { alert('Bank Merchant ID is required'); return; }
    // if (!form.merchantVpa.trim()) { alert('Merchant VPA is required'); return; }
    // if (!form.apiKey.trim()) { alert('Bank API Key is required'); return; }
    // if (!form.callbackUrl.trim()) { alert('Callback URL is required'); return; }

    // Provider-specific validations
    if (form.provider === "ICICI") {

      if (!form.merchantId.trim()) {
        alert("Bank Merchant ID is required");
        return;
      }

      if (!form.merchantVpa.trim()) {
        alert("Merchant VPA is required");
        return;
      }

      if (!form.apiKey.trim()) {
        alert("Bank API Key is required");
        return;
      }

      if (!form.callbackUrl.trim()) {
        alert("Callback URL is required");
        return;
      }

    }

    if (form.provider === "EASEBUZZ") {
      if (!form.apiKey.trim()) {
        alert("Merchant Key is required");
        return;
      }
      if (!form.merchantSalt.trim()) {
        alert("Merchant Salt is required");
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        businessName: form.businessName,
        domain: form.domain,
        preferredBank: form.preferredBank,
        webhookUrl: form.webhookUrl,
        upiId: form.merchantVpa,
        bankConfig: buildBankConfig(),
        iciciPublicKeyName: form.iciciPublicKeyName,
        merchantPrivateKeyName: form.merchantPrivateKeyName,
        merchantCertificateName: form.merchantCertificateName,
        privateIp: form.privateIp,
        bankEndpoint: form.bankEndpoint
      };
      if (editingId) {
        // Update existing merchant
        const updateRes = await axios.put(`/api/admin/merchants/${editingId}`, payload, { headers: headers() });
        // Save gateway config if privateIp is set
        if (form.privateIp.trim()) {
          await axios.post('/api/merchant/config/batch-gateway-config', [{
            merchantId: editingId,
            privateIp: form.privateIp,
            domain: form.domain,
            bankEndpoint: form.bankEndpoint,
            iciciPublicKeyName: form.iciciPublicKeyName,
            merchantPrivateKeyName: form.merchantPrivateKeyName,
            merchantCertificateName: form.merchantCertificateName
          }], { headers: { ...headers(), 'Content-Type': 'application/json' } });
        }
        // Save payout config - always save when editing (ensures removals are persisted)
        const hasExistingConfig = form.payoutAggrId.trim() || form.payoutCorpId.trim() ||
          form.payoutApiKey.trim() || form.payoutApiUrl.trim() || form.payoutUserId.trim() ||
          form.payoutUrn.trim() || form.payoutDebitAcc.trim() || form.payoutAggrName.trim() ||
          form.payoutPublicKeyName.trim();
        if (hasExistingConfig) {
          // Validate mandatory payout fields based on provider
          if (form.payoutProvider === 'CASHFREE') {
            if (!form.cashfreeClientId.trim()) { alert('Payout Config: Cashfree Client ID is required'); setSaving(false); return; }
            if (!form.cashfreeSecretKey.trim()) { alert('Payout Config: Cashfree Secret Key is required'); setSaving(false); return; }
          } else if (form.payoutProvider === 'ICICI_CIB' || form.payoutProvider === 'ICICI_IMPS') {
            if (!form.payoutAggrId.trim()) { alert('Payout Config: AGGRID (Aggregator ID) is required'); setSaving(false); return; }
            if (!form.payoutCorpId.trim()) { alert('Payout Config: CORPID (Corporate ID) is required'); setSaving(false); return; }
            if (!form.payoutUserId.trim()) { alert('Payout Config: USERID (User ID) is required'); setSaving(false); return; }
            if (!form.payoutUrn.trim()) { alert('Payout Config: URN is required'); setSaving(false); return; }
            if (!form.payoutDebitAcc.trim()) { alert('Payout Config: Debit Account (DEBITACC) is required'); setSaving(false); return; }
            if (!form.payoutApiKey.trim()) { alert('Payout Config: Payout API Key is required'); setSaving(false); return; }
            if (!form.payoutApiUrl.trim()) { alert('Payout Config: Payout API URL is required'); setSaving(false); return; }
          }
          const payoutSaveRes = await axios.post('/api/admin/payout-config', {
            merchantId: String(editingId),
            aggrId: form.payoutAggrId,
            aggrName: form.payoutAggrName,
            corpId: form.payoutCorpId,
            userId: form.payoutUserId,
            urn: form.payoutUrn,
            debitAcc: form.payoutDebitAcc,
            workflowReqd: form.payoutWorkflowReqd,
            preferredProvider: form.payoutProvider,
            perTransactionLimit: form.payoutPerTxnLimit || null,
            dailyLimit: form.payoutDailyLimit || null,
            payoutApiKey: form.payoutApiKey,
            payoutApiUrl: form.payoutApiUrl,
            payoutPublicKeyName: form.payoutPublicKeyName,
            cashfreeClientId: form.cashfreeClientId || null,
            cashfreeSecretKey: form.cashfreeSecretKey || null,
            cashfreeApiVersion: form.cashfreeApiVersion || null
          }, { headers: { ...headers(), 'Content-Type': 'application/json' } });
          console.log('[saveMerchant] Payout config save response:', payoutSaveRes.data);
        }
        // Fetch fresh merchant data directly by ID and reopen form (this will also load payout config)
        const freshRes = await axios.get(`/api/admin/merchants/${editingId}`, { headers: headers() });
        if (freshRes.data.success) await openEdit(freshRes.data.data);
        fetchMerchants(); // refresh the list in background
        alert('Merchant and payout config saved successfully!');
        return; // don't fall through to resetForm
      } else {
        // Create new merchant
        const createRes = await axios.post('/api/admin/merchants', payload, { headers: headers() });
        const newMerchantId = createRes.data?.data?.id;
        if (newMerchantId && form.privateIp.trim()) {
          await axios.post('/api/merchant/config/batch-gateway-config', [{
            merchantId: newMerchantId,
            privateIp: form.privateIp,
            domain: form.domain,
            bankEndpoint: form.bankEndpoint,
            iciciPublicKeyName: form.iciciPublicKeyName,
            merchantPrivateKeyName: form.merchantPrivateKeyName,
            merchantCertificateName: form.merchantCertificateName
          }], { headers: { ...headers(), 'Content-Type': 'application/json' } });
        }
        // Save payout config for new merchant
        if (newMerchantId && (form.payoutAggrId.trim() || form.payoutCorpId.trim() || form.cashfreeClientId.trim())) {
          await axios.post('/api/admin/payout-config', {
            merchantId: String(newMerchantId),
            aggrId: form.payoutAggrId,
            aggrName: form.payoutAggrName,
            corpId: form.payoutCorpId,
            userId: form.payoutUserId,
            urn: form.payoutUrn,
            debitAcc: form.payoutDebitAcc,
            workflowReqd: form.payoutWorkflowReqd,
            preferredProvider: form.payoutProvider,
            perTransactionLimit: form.payoutPerTxnLimit || null,
            dailyLimit: form.payoutDailyLimit || null,
            payoutApiKey: form.payoutApiKey,
            payoutApiUrl: form.payoutApiUrl,
            payoutPublicKeyName: form.payoutPublicKeyName,
            cashfreeClientId: form.cashfreeClientId || null,
            cashfreeSecretKey: form.cashfreeSecretKey || null,
            cashfreeApiVersion: form.cashfreeApiVersion || null
          }, { headers: { ...headers(), 'Content-Type': 'application/json' } });
        }
        fetchMerchants();
        alert('Merchant created successfully!');
        resetForm(); // only reset for new merchant
      }
    } catch (e) {
      alert('Failed to save: ' + (e.response?.data?.message || e.message));
    } finally { setSaving(false); }
  };

  const deleteMerchant = async (id, name) => {
    if (!window.confirm(`Deactivate merchant "${name}"?`)) return;
    try {
      await axios.delete(`/api/admin/merchants/${id}`, { headers: headers() });
      fetchMerchants();
    } catch (e) { alert('Failed to delete: ' + e.message); }
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      await axios.put(`/api/admin/merchants/${id}/status`, { isActive: !currentStatus }, { headers: headers() });
      fetchMerchants();
    } catch (e) { alert('Failed to toggle status: ' + e.message); }
  };

  const parseBankConfig = (json) => {
    if (!json) return {};
    try {
      let parsed = JSON.parse(json);
      if (typeof parsed === 'string') parsed = JSON.parse(parsed);
      return parsed || {};
    } catch (e) { return {}; }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }} role="status" aria-live="polite">Loading merchants...</div>;

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>Merchant Management</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={openAdd}
            style={{ padding: '10px 20px', background: '#007a75', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
            + Add Merchant
          </button>
        </div>
      </div>

      {/* Merchant Form */}
      {showForm && (
        <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h3 style={{ marginTop: 0 }}>{editingId ? 'Edit Merchant' : 'Add New Merchant'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Business Name <span style={{ color: '#d32f2f' }}>*</span></label>
              <input type="text" value={form.businessName} onChange={e => setForm({ ...form, businessName: e.target.value })}
                placeholder="e.g. Zenxa Solutions" style={inputStyle} required />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Payment Domain <span style={{ color: '#d32f2f' }}>*</span></label>
              <input type="text" value={form.domain} onChange={e => setForm({ ...form, domain: e.target.value })}
                placeholder="e.g. payment.zenxa.com (comma-separated for multiple)" style={inputStyle} required />
              <small style={{ color: '#666', fontSize: '12px' }}>
                Comma-separated domains. These are used for ICICI callback URL and CORS. Example: payment.zenxa.com,zenxa.com
              </small>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Preferred Bank <span style={{ color: '#d32f2f' }}>*</span></label>
              <input type="text" value={form.preferredBank} onChange={e => setForm({ ...form, preferredBank: e.target.value.toUpperCase(), provider: e.target.value.toUpperCase() })} 
                style={inputStyle} required placeholder="e.g. ICICI, EASEBUZZ, CASHFREE" list="bank-suggestions" />
              <datalist id="bank-suggestions">
                <option value="ICICI" />
                <option value="EASEBUZZ" />
                <option value="CASHFREE" />
                <option value="RAZORPAY" />
                <option value="HDFC" />
                <option value="AXIS" />
                <option value="KOTAK" />
              </datalist>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Provider (in bankConfig) <span style={{ color: '#d32f2f' }}>*</span></label>
              <input type="text" value={form.provider} onChange={e => setForm({ ...form, provider: e.target.value.toUpperCase() })} 
                style={inputStyle} required placeholder="e.g. ICICI, EASEBUZZ, CASHFREE" list="provider-suggestions-mgmt" />
              <datalist id="provider-suggestions-mgmt">
                <option value="ICICI" />
                <option value="EASEBUZZ" />
                <option value="CASHFREE" />
                <option value="RAZORPAY" />
                <option value="HDFC" />
                <option value="AXIS" />
                <option value="KOTAK" />
              </datalist>
            </div>
            {form.provider === "ICICI" && (
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>
                  Bank Merchant ID <span style={{ color: '#d32f2f' }}>*</span>
                </label>

                <input
                  type="text"
                  value={form.merchantId}
                  onChange={e => setForm({ ...form, merchantId: e.target.value })}
                  placeholder="e.g. 613254"
                  style={inputStyle}
                  required
                />
              </div>
            )}

            {form.provider === "ICICI" && (
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>
                  Merchant VPA <span style={{ color: '#d32f2f' }}>*</span>
                </label>

                <input
                  type="text"
                  value={form.merchantVpa}
                  onChange={e => setForm({ ...form, merchantVpa: e.target.value })}
                  placeholder="e.g. clickpayuat@icici"
                  style={inputStyle}
                  required
                />
              </div>
            )}
            {form.provider !== "CASHFREE" && (
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>
                {
                  form.provider === "EASEBUZZ"
                    ?
                    "Merchant Key"
                    :
                    "Bank API Key"
                }
                <span style={{ color: '#d32f2f' }}> *</span>
              </label>
              <input type="text" value={form.apiKey} onChange={e => setForm({ ...form, apiKey: e.target.value })}
                placeholder={
                  form.provider === "EASEBUZZ"
                    ?
                    "Enter Easebuzz Merchant Key"
                    :
                    "e.g. OW6D1jWAHvZSVZGeIjrmS5XaALjBydgR"
                }
                style={inputStyle}
                required
              />
            </div>
            )}

            {form.provider === "EASEBUZZ" && (
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>
                  Merchant Salt <span style={{ color: '#d32f2f' }}>*</span>
                </label>
                <input
                  type="text"
                  value={form.merchantSalt}
                  onChange={e => setForm({ ...form, merchantSalt: e.target.value })}
                  placeholder="Enter Easebuzz Merchant Salt"
                  style={inputStyle}
                />
              </div>
            )}

            {form.provider === "EASEBUZZ" && (
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>
                  Environment <span style={{ color: '#d32f2f' }}>*</span>
                </label>
                <select value={form.apiUrl} onChange={e => setForm({ ...form, apiUrl: e.target.value })} style={inputStyle}>
                  <option value="uat">UAT (Test)</option>
                  <option value="prod">Production</option>
                </select>
                <small style={{ color: '#666', fontSize: '11px' }}>
                  UAT → testpay.easebuzz.in &nbsp;|&nbsp; Production → pay.easebuzz.in
                </small>
              </div>
            )}

            {form.provider === "EASEBUZZ" && (
              <div style={{ gridColumn: '1 / -1', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '6px', padding: '12px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#e65100' }}>
                  📋 ClickPay Webhook URL — paste this in merchant's Easebuzz dashboard
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="text"
                    readOnly
                    value="https://api.clickpaysolution.com/api/payment/easebuzz/webhook"
                    style={{ ...inputStyle, background: '#f5f5f5', color: '#333', fontFamily: 'monospace', fontSize: '13px', flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => { navigator.clipboard.writeText('https://api.clickpaysolution.com/api/payment/easebuzz/webhook'); alert('Copied!'); }}
                    style={{ padding: '8px 14px', background: '#007a75', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Copy
                  </button>
                </div>
                <small style={{ color: '#666', fontSize: '11px', display: 'block', marginTop: '4px' }}>
                  Easebuzz dashboard → Account Settings → Webhook → Transaction Webhook URL → paste above URL
                </small>
              </div>
            )}

            {form.provider === "CASHFREE" && (
              <div style={{ gridColumn: '1 / -1', background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '6px', padding: '12px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#1565c0' }}>Cashfree Configuration</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>App ID (x-client-id) *</label>
                    <input type="text" value={form.apiKey} onChange={e => setForm({ ...form, apiKey: e.target.value })}
                      placeholder="Enter Cashfree App ID" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Secret Key (x-client-secret) *</label>
                    <input type="password" value={form.apiSecret || ''} onChange={e => setForm({ ...form, apiSecret: e.target.value })}
                      placeholder="Enter Cashfree Secret Key" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>API Version</label>
                    <input type="text" value={form.apiVersion || ''} onChange={e => setForm({ ...form, apiVersion: e.target.value })}
                      placeholder="e.g. 2025-01-01" style={inputStyle} />
                  </div>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Webhook/Callback URL *</label>
                  <input type="text" value={form.callbackUrl || ''} onChange={e => setForm({ ...form, callbackUrl: e.target.value })}
                    placeholder="https://api-nonprod.clickpaysolution.com/api/payment/cashfree/webhook" style={inputStyle} />
                  <small style={{ color: '#666', fontSize: '11px', display: 'block', marginTop: '4px' }}>
                    This URL is sent to Cashfree with every order. Also configure it in Cashfree dashboard → Developers → Webhooks.
                  </small>
                </div>
              </div>
            )}

            {form.provider === "ICICI" && (
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>
                  Callback URL <span style={{ color: '#d32f2f' }}>*</span>
                </label>

                <input
                  type="text"
                  value={form.callbackUrl}
                  onChange={e => setForm({ ...form, callbackUrl: e.target.value })}
                  placeholder="e.g. https://payment.zenxa.com/api/payment/icici/upi/callback"
                  style={inputStyle}
                  required
                />
              </div>
            )}

            {form.provider === "ICICI" && (
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>
                  Bank API URL
                  <span style={{ color: '#888', fontSize: '12px', fontWeight: 'normal' }}> (optional)</span>
                </label>
                <input
                  type="text"
                  value={form.apiUrl}
                  onChange={e => setForm({ ...form, apiUrl: e.target.value })}
                  placeholder="(Optional — uses global Bank API Config if empty)"
                  style={{ ...inputStyle, background: '#f8f9fa' }}
                />
                <span style={{ fontSize: '11px', color: '#666' }}>
                  Leave empty to use the global Bank API Config
                </span>
              </div>
            )}

            {form.provider === "ICICI" && (
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Webhook URL</label>
                <input type="text" value={form.webhookUrl} onChange={e => setForm({ ...form, webhookUrl: e.target.value })}
                  placeholder="e.g. https://zenxa.com/payment-webhook" style={inputStyle} />
              </div>
            )}
          </div>

          {/* Gateway Routing Configuration — for banks requiring mTLS/IP binding */}
          {form.provider !== "CASHFREE" && form.provider !== "EASEBUZZ" && (
          <div style={{ marginTop: '20px', padding: '16px', background: '#f0f7ff', borderRadius: '8px', border: '1px solid #b8daff' }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#004085' }}>🔀 Gateway Routing Configuration</h4>
            <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#666' }}>
              Configure multi-tenant routing through the Bank Gateway Service. Set the private IP, certificate, and bank endpoint per merchant. Leave Private IP empty to use direct bank integration.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Private IP</label>
                <input type="text" value={form.privateIp} onChange={e => setForm({ ...form, privateIp: e.target.value })}
                  placeholder="e.g. 10.0.1.10" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Bank Endpoint</label>
                <input type="text" value={form.bankEndpoint} onChange={e => setForm({ ...form, bankEndpoint: e.target.value })}
                  placeholder="e.g. https://apibankingone.icicibank.com" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Bank Public Key (.cer)</label>
                <input type="text" value={form.iciciPublicKeyName} onChange={e => setForm({ ...form, iciciPublicKeyName: e.target.value })}
                  placeholder="e.g. icici_pub.cer" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Merchant Private Key (.key/.pem)</label>
                <input type="text" value={form.merchantPrivateKeyName} onChange={e => setForm({ ...form, merchantPrivateKeyName: e.target.value })}
                  placeholder="e.g. merchant_priv.pem" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Merchant Certificate (.cer)</label>
                <input type="text" value={form.merchantCertificateName} onChange={e => setForm({ ...form, merchantCertificateName: e.target.value })}
                  placeholder="e.g. merchant_cert.cer" style={inputStyle} />
              </div>
            </div>
            <p style={{ margin: '12px 0 0 0', fontSize: '12px', color: '#004085', fontStyle: 'italic' }}>
              📁 Upload these 3 files to the /certs/ directory on the server.
            </p>
            {editingId && form.provider === 'ICICI' && (
              <div style={{ marginTop: '12px', padding: '10px', background: '#e8f4fd', borderRadius: '6px', border: '1px solid #b8daff' }}>
                <p style={{ margin: '0 0 6px 0', fontSize: '12px', fontWeight: '600', color: '#004085' }}>
                  📋 ICICI Callback URLs — share with bank
                </p>
                {form.username && (
                  <div style={{ marginBottom: '6px' }}>
                    <span style={{ fontSize: '11px', color: '#155724', fontWeight: '600' }}>
                      ✅ Username-based (use this BEFORE merchantId is assigned):
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                      <code style={{ fontSize: '11px', background: '#fff', padding: '3px 6px', borderRadius: '3px', flex: 1, wordBreak: 'break-all' }}>
                        https://api.clickpaysolution.com/api/payment/icici/upi/callback/{form.username}
                      </code>
                      <button type="button" onClick={() => { navigator.clipboard.writeText(`https://api.clickpaysolution.com/api/payment/icici/upi/callback/${form.username}`); alert('Copied!'); }}
                        style={{ padding: '3px 8px', background: '#007a75', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', whiteSpace: 'nowrap' }}>
                        Copy
                      </button>
                    </div>
                  </div>
                )}
                {form.domain && (
                  <div>
                    <span style={{ fontSize: '11px', color: '#004085' }}>
                      Domain-based (use after domain is configured):
                    </span>
                    <code style={{ display: 'block', fontSize: '11px', background: '#fff', padding: '3px 6px', borderRadius: '3px', marginTop: '2px', wordBreak: 'break-all' }}>
                      https://{form.domain.split(',')[0].trim()}/api/payment/icici/upi/callback
                    </code>
                  </div>
                )}
                {editingId && (
                  <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: '#666' }}>
                    Test URL: <a href={`/api/payment/icici/upi/callback/${form.username}/test`} target="_blank" rel="noreferrer" style={{ color: '#007a75' }}>
                      click to verify
                    </a>
                  </p>
                )}
              </div>
            )}
            {editingId && form.provider === 'ICICI' && !form.username && !form.domain && (
              <p style={{ margin: '12px 0 0 0', fontSize: '12px', color: '#856404' }}>
                ⚠ No username or domain configured — cannot generate callback URL
              </p>
            )}
          </div>
          )}

          {/* Payout CIB Configuration — for banks requiring CIB credentials */}
          {form.provider !== "CASHFREE" && form.provider !== "EASEBUZZ" && (
          <div style={{ marginTop: '20px', padding: '16px', background: '#f0fff4', borderRadius: '8px', border: '1px solid #b2dfdb' }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#1b5e20' }}>💸 Payout Configuration (ICICI CIB)</h4>
            <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#666' }}>
              Configure ICICI CIB credentials for payout processing. These are provided by ICICI during payout onboarding.
              Fields marked with <span style={{ color: '#d32f2f' }}>*</span> are mandatory.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Payout API Key <span style={{ color: '#d32f2f' }}>*</span></label>
                <input type="text" value={form.payoutApiKey} onChange={e => setForm({ ...form, payoutApiKey: e.target.value })}
                  placeholder="e.g. lvY7dgGzkwA7aNwwA9HEvXOxHG9zhA8k" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Payout API URL <span style={{ color: '#d32f2f' }}>*</span></label>
                <input type="text" value={form.payoutApiUrl} onChange={e => setForm({ ...form, payoutApiUrl: e.target.value })}
                  placeholder="e.g. https://apibankingonesandbox.icici.bank.in/api/Corporate/CIB_SV/v1/Transaction" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>ICICI CIB Public Key Name</label>
                <input type="text" value={form.payoutPublicKeyName} onChange={e => setForm({ ...form, payoutPublicKeyName: e.target.value })}
                  placeholder="e.g. icici_cib_pub.cer" style={inputStyle} />
                <small style={{ color: '#666', fontSize: '11px' }}>File must be in /certs/ folder on server</small>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Payout Provider</label>
                <select value={form.payoutProvider} onChange={e => setForm({ ...form, payoutProvider: e.target.value })} style={inputStyle}>
                  <option value="ICICI_CIB">ICICI CIB</option>
                  <option value="ICICI_IMPS">ICICI IMPS (Legacy)</option>
                  <option value="EASEBUZZ_WIRE">Easebuzz Wire</option>
                  <option value="CASHFREE">Cashfree</option>
                </select>
              </div>

              {/* Cashfree Payout Fields */}
              {form.payoutProvider === 'CASHFREE' && (
                <>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Cashfree Client ID *</label>
                    <input type="text" value={form.cashfreeClientId || ''} onChange={e => setForm({ ...form, cashfreeClientId: e.target.value })}
                      placeholder="Cashfree App ID (x-client-id)" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Cashfree Secret Key *</label>
                    <input type="password" value={form.cashfreeSecretKey || ''} onChange={e => setForm({ ...form, cashfreeSecretKey: e.target.value })}
                      placeholder="Cashfree Secret Key (x-client-secret)" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>API Version</label>
                    <input type="text" value={form.cashfreeApiVersion || ''} onChange={e => setForm({ ...form, cashfreeApiVersion: e.target.value })}
                      placeholder="e.g. 2024-01-01" style={inputStyle} />
                  </div>
                </>
              )}

              {/* ICICI CIB / IMPS Fields */}
              {(form.payoutProvider === 'ICICI_CIB' || form.payoutProvider === 'ICICI_IMPS') && (
              <>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>AGGRID (Aggregator ID) <span style={{ color: '#d32f2f' }}>*</span></label>
                <input type="text" value={form.payoutAggrId} onChange={e => setForm({ ...form, payoutAggrId: e.target.value })}
                  placeholder="e.g. TXBCIBTEST001" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>AGGRNAME (Aggregator Name)</label>
                <input type="text" value={form.payoutAggrName} onChange={e => setForm({ ...form, payoutAggrName: e.target.value })}
                  placeholder="e.g. CIBTESTING" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>CORPID (Corporate ID) <span style={{ color: '#d32f2f' }}>*</span></label>
                <input type="text" value={form.payoutCorpId} onChange={e => setForm({ ...form, payoutCorpId: e.target.value })}
                  placeholder="e.g. TXBCORP1" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>USERID (User ID) <span style={{ color: '#d32f2f' }}>*</span></label>
                <input type="text" value={form.payoutUserId} onChange={e => setForm({ ...form, payoutUserId: e.target.value })}
                  placeholder="e.g. USER1" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>URN <span style={{ color: '#d32f2f' }}>*</span></label>
                <input type="text" value={form.payoutUrn} onChange={e => setForm({ ...form, payoutUrn: e.target.value })}
                  placeholder="e.g. TESTING123" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Debit Account (DEBITACC) <span style={{ color: '#d32f2f' }}>*</span></label>
                <input type="text" value={form.payoutDebitAcc} onChange={e => setForm({ ...form, payoutDebitAcc: e.target.value })}
                  placeholder="e.g. 010205001809" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>WORKFLOW_REQD</label>
                <select value={form.payoutWorkflowReqd} onChange={e => setForm({ ...form, payoutWorkflowReqd: e.target.value })} style={inputStyle}>
                  <option value="N">N - STP (Auto-process)</option>
                  <option value="Y">Y - Maker-Checker (CIB Portal Approval)</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Per-Transaction Limit (₹)</label>
                <input type="number" value={form.payoutPerTxnLimit} onChange={e => setForm({ ...form, payoutPerTxnLimit: e.target.value })}
                  placeholder="e.g. 500000" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Daily Limit (₹)</label>
                <input type="number" value={form.payoutDailyLimit} onChange={e => setForm({ ...form, payoutDailyLimit: e.target.value })}
                  placeholder="e.g. 10000000" style={inputStyle} />
              </div>
              </>
              )}
            </div>
          </div>
          )}
          <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={resetForm} style={{ padding: '10px 24px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
            <button onClick={saveMerchant} disabled={saving}
              style={{ padding: '10px 24px', background: '#007a75', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
              {saving ? 'Saving...' : (editingId ? 'Update Merchant' : 'Create Merchant')}
            </button>
          </div>
        </div>
      )}

      {/* Domain management for existing merchants */}
      {showForm && editingId && (
        <div style={{ marginTop: '20px' }}>
          <AllowedDomainsManager merchantId={editingId} isAdmin={true} />
        </div>
      )}

      {/* Merchants Table */}
      <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', overflow: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }} role="table">
          <caption className="sr-only">Merchant Management</caption>
          <thead>
            <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              <th scope="col" style={thStyle}>ID</th>
              <th scope="col" style={thStyle}>Business Name</th>
              <th scope="col" style={thStyle}>Domain</th>
              <th scope="col" style={thStyle}>Provider</th>
              <th scope="col" style={thStyle}>Merchant VPA</th>
              <th scope="col" style={thStyle}>Status</th>
              <th scope="col" style={thStyle}>Gateway</th>
              <th scope="col" style={thStyle}>Created</th>
              <th scope="col" style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {merchants.length === 0 ? (
              <tr><td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#767676' }}>No merchants found. Click "+ Add Merchant" to create one.</td></tr>
            ) : merchants
              .slice((merchantPage - 1) * MERCHANT_PAGE_SIZE, merchantPage * MERCHANT_PAGE_SIZE)
              .map(m => {
                const bc = parseBankConfig(m.bankConfig);
                return (
                  <tr key={m.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={tdStyle}>{m.id}</td>
                    <td style={tdStyle}><strong>{m.businessName}</strong></td>
                    <td style={{ ...tdStyle, color: m.domain ? '#28a745' : '#767676', fontSize: '13px' }}>{m.domain || 'Not set'}</td>
                    <td style={tdStyle}>
                      <span style={{
                        padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '600',
                        background: (bc.provider || m.preferredBank || '').startsWith('MOCK') ? '#fff3cd' : '#d4edda',
                        color: (bc.provider || m.preferredBank || '').startsWith('MOCK') ? '#856404' : '#155724'
                      }}>
                        {bc.provider || m.preferredBank || 'N/A'}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, fontSize: '13px' }}>{bc.merchantVpa || m.upiId || '-'}</td>
                    <td style={tdStyle}>
                      <span style={{
                        padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '600',
                        background: m.isActive ? '#d4edda' : '#f8d7da', color: m.isActive ? '#155724' : '#721c24'
                      }}>
                        {m.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      {m.privateIp ? (
                        <span style={{
                          padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '600',
                          background: '#cce5ff', color: '#004085'
                        }}>
                          {m.privateIp}
                        </span>
                      ) : (
                        <span style={{ color: '#767676', fontSize: '12px' }}>Direct</span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontSize: '12px' }}>{m.createdAt ? new Date(m.createdAt).toLocaleDateString() : '-'}</td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <button onClick={() => openEdit(m)} style={btnStyle('#007a75')}>Edit</button>
                        <button onClick={() => toggleStatus(m.id, m.isActive)} style={btnStyle(m.isActive ? '#dc3545' : '#28a745')}>
                          {m.isActive ? 'Disable' : 'Enable'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
        <Pagination
          currentPage={merchantPage}
          totalPages={Math.max(1, Math.ceil(merchants.length / MERCHANT_PAGE_SIZE))}
          goToPage={setMerchantPage}
          total={merchants.length}
          pageSize={MERCHANT_PAGE_SIZE}
        />
      </div>

      {/* Summary */}
      <div style={{ marginTop: '20px', padding: '16px', background: '#f8f9fa', borderRadius: '8px', display: 'flex', gap: '30px' }}>
        <span>Total: <strong>{merchants.length}</strong></span>
        <span>Active: <strong style={{ color: '#28a745' }}>{merchants.filter(m => m.isActive).length}</strong></span>
        <span>With Domain: <strong style={{ color: '#007bff' }}>{merchants.filter(m => m.domain).length}</strong></span>
        <span>Real Bank: <strong style={{ color: '#155724' }}>{merchants.filter(m => { const bc = parseBankConfig(m.bankConfig); return bc.provider && !bc.provider.startsWith('MOCK'); }).length}</strong></span>
        <span>Gateway Routed: <strong style={{ color: '#004085' }}>{merchants.filter(m => m.privateIp).length}</strong></span>
      </div>
    </div>
  );
};

const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };
const thStyle = { padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' };
const tdStyle = { padding: '12px 16px', verticalAlign: 'middle' };
const btnStyle = (bg) => ({ padding: '5px 12px', background: bg, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' });

export default AdminMerchantManagement;
